import { matchesPath } from '../nav-match.util';
import type { SidebarNavAccess } from '../use-sidebar-nav-access.hook';

/**
 * Every page the tabbed sidebar and the command palette go to, by a stable
 * id the saved layout uses. Sequencing settings and the extension installer
 * are not pages here: they live in Settings and in Integrations. Settings is
 * the footer's gear, never a tile or a row.
 */
export const SIDEBAR_PAGE_IDS = [
  'find-people',
  'lead-lists',
  'inbox',
  'sequences',
  'tasks',
  'home',
  'people',
  'companies',
  'agents',
  'reports',
  'integrations',
  'settings',
] as const;

export type SidebarPageId = (typeof SIDEBAR_PAGE_IDS)[number];

export interface SidebarDestination {
  id: SidebarPageId;
  href: string;
  label: string;
  /** A DynamicIcon name. */
  iconName: string;
  /** Match only this path, not its detail routes. */
  exact?: boolean;
  /** Other paths the page covers, such as settings that moved under it. */
  also?: readonly string[];
  /** The note a locked entry shows. */
  lockedTooltip?: string;
}

export const SIDEBAR_DESTINATIONS: Record<SidebarPageId, SidebarDestination> = {
  'find-people': {
    id: 'find-people',
    href: '/finder',
    label: 'Find People',
    iconName: 'user-magnifying-glass',
  },
  'lead-lists': {
    id: 'lead-lists',
    href: '/lists',
    label: 'Lead Lists',
    iconName: 'list-bullets',
    // People and Companies are tabs on the Lead Lists page, so its tile
    // stays current there.
    also: ['/people', '/companies'],
  },
  inbox: {
    id: 'inbox',
    href: '/inbox',
    label: 'Inbox',
    iconName: 'inbox',
    lockedTooltip: 'Choose a plan with Sequences',
  },
  sequences: {
    id: 'sequences',
    href: '/sequences',
    label: 'Sequences',
    iconName: 'paper-plane-clu',
    lockedTooltip: 'Choose a plan with Sequences',
  },
  tasks: {
    id: 'tasks',
    href: '/tasks',
    label: 'Tasks',
    iconName: 'list-check',
    lockedTooltip: 'Choose a plan with Sequences',
  },
  home: {
    id: 'home',
    href: '/',
    label: 'Home',
    iconName: 'house',
    exact: true,
  },
  people: {
    id: 'people',
    href: '/people',
    label: 'People',
    iconName: 'address-book',
  },
  companies: {
    id: 'companies',
    href: '/companies',
    label: 'Companies',
    iconName: 'buildings',
  },
  agents: {
    id: 'agents',
    href: '/agents',
    label: 'Agents',
    iconName: 'robot',
  },
  reports: {
    id: 'reports',
    href: '/reports',
    label: 'Reports',
    iconName: 'chart-user',
  },
  integrations: {
    id: 'integrations',
    href: '/integrations',
    label: 'Integrations',
    iconName: 'puzzle-piece',
    lockedTooltip: 'Available on Pro+',
  },
  settings: {
    id: 'settings',
    href: '/settings',
    label: 'Settings',
    iconName: 'gear',
    // Sequencing settings, billing and API keys sit in the settings nav, so
    // the gear stays current on their pages.
    also: ['/sequencing/settings', '/plans', '/api-keys'],
  },
};

export function isSidebarPageId(value: unknown): value is SidebarPageId {
  return (
    typeof value === 'string' &&
    (SIDEBAR_PAGE_IDS as readonly string[]).includes(value)
  );
}

/**
 * Whether a page can be a tile or a row. Settings cannot: the footer's gear
 * goes there. People and Companies cannot either: they are tabs on the Lead
 * Lists page. None of them is pinned, offered to add back, or shown as the
 * page tab; the command palette still opens them.
 */
export function isPinnablePage(id: SidebarPageId): boolean {
  return id !== 'settings' && id !== 'people' && id !== 'companies';
}

/**
 * Whether a page shows for this user, and whether it carries the upgrade
 * lock, by the sidebar's access rules (see useSidebarNavAccess).
 */
export function destinationState(
  id: SidebarPageId,
  access: SidebarNavAccess
): { visible: boolean; locked: boolean } {
  switch (id) {
    // Without the plan the three stay, locked, and open the upgrade card.
    case 'sequences':
    case 'inbox':
    case 'tasks':
      return {
        visible: access.sequencingEnabled,
        locked: access.sequencingLocked,
      };
    case 'people':
    case 'companies':
      return { visible: access.showDirectory, locked: false };
    case 'integrations':
      return { visible: true, locked: access.integrationsLocked };
    default:
      return { visible: true, locked: false };
  }
}

/**
 * The paths a page or pin covers, each with the key it selects: its own
 * href, then any other paths it covers.
 */
export function coveredPaths<K>(
  entries: readonly {
    key: K;
    href?: string;
    exact?: boolean;
    also?: readonly string[];
  }[]
): { key: K; href: string; exact?: boolean }[] {
  return entries.flatMap((entry) =>
    entry.href
      ? [
          { key: entry.key, href: entry.href, exact: entry.exact },
          ...(entry.also ?? []).map((href) => ({ key: entry.key, href })),
        ]
      : []
  );
}

/** The pages this user can see, in registry order. */
export function visibleDestinations(
  access: SidebarNavAccess
): SidebarDestination[] {
  return SIDEBAR_PAGE_IDS.filter(
    (id) => destinationState(id, access).visible
  ).map((id) => SIDEBAR_DESTINATIONS[id]);
}

/** The pages this user can pin: the ones they see, less Settings. */
export function pinnableDestinations(
  access: SidebarNavAccess
): SidebarDestination[] {
  return visibleDestinations(access).filter((page) => isPinnablePage(page.id));
}

/** Whether the path is a Settings page: any path its destination covers. */
export function isSettingsPath(pathname: string): boolean {
  const settings = SIDEBAR_DESTINATIONS.settings;
  return coveredPaths([{ ...settings, key: settings.id }]).some((path) =>
    matchesPath(pathname, path.href, path.exact)
  );
}
